/**
 * FlexibleWorkingRequestForm.jsx
 * 
 * Employee form to submit a flexible working request.
 * Implements Employment Rights Act 1996 (as amended 2024):
 * - Day-one right to request
 * - 2 requests per 12 months
 * - 2-month response deadline
 * 
 * Features:
 * - Eligibility check (2 per year limit)
 * - Flexibility type selection with descriptions
 * - Current vs proposed pattern comparison
 * - Impact assessment guidance
 * - Clear explanation of process and rights
 */

import React, { useState, useEffect } from 'react';
import api from './axiosConfig';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Info,
  Schedule,
  Send,
  ArrowBack,
  ArrowForward,
  AccessTime,
  Gavel,
  Work,
  Home,
  CalendarToday,
  Help,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

// ============================================================
// FLEXIBILITY TYPES
// ============================================================

const FLEXIBILITY_TYPES = {
  part_time: {
    key: 'part_time',
    name: 'Part-Time Working',
    icon: '⏰',
    description: 'Reduce your total working hours',
    examples: 'e.g., Work 3 days per week instead of 5, or reduce daily hours',
    considerations: [
      'Consider which days/hours work best for business needs',
      'Think about how your workload could be adjusted',
      'May affect your salary proportionally'
    ]
  },
  compressed: {
    key: 'compressed',
    name: 'Compressed Hours',
    icon: '📅',
    description: 'Work your full hours over fewer days',
    examples: 'e.g., 4 x 10-hour days instead of 5 x 8-hour days',
    considerations: [
      'Longer days may affect work-life balance',
      'Consider client/salon opening hours',
      'May need to adjust break patterns'
    ]
  },
  flexitime: {
    key: 'flexitime',
    name: 'Flexitime',
    icon: '🔄',
    description: 'Flexible start and finish times around core hours',
    examples: 'e.g., Start between 7am-10am, finish between 3pm-6pm',
    considerations: [
      'What core hours could you guarantee presence?',
      'How would this affect team meetings and clients?',
      'Consider handover arrangements'
    ]
  },
  remote: {
    key: 'remote',
    name: 'Remote Working',
    icon: '🏠',
    description: 'Work from home or another location full-time',
    examples: 'e.g., Work entirely from home',
    considerations: [
      'What tasks can be done remotely?',
      'How would you stay connected with the team?',
      'Equipment and workspace requirements'
    ]
  },
  hybrid: {
    key: 'hybrid',
    name: 'Hybrid Working',
    icon: '🏢🏠',
    description: 'Mix of workplace and remote working',
    examples: 'e.g., 2 days in salon, 3 days remote (for admin roles)',
    considerations: [
      'Which days would you be on-site?',
      'How would this affect team collaboration?',
      'Consider client-facing requirements'
    ]
  },
  job_share: {
    key: 'job_share',
    name: 'Job Share',
    icon: '👥',
    description: 'Share your role with another employee',
    examples: 'e.g., Split full-time role 50/50 with a colleague',
    considerations: [
      'Do you have a potential job share partner?',
      'How would handovers work?',
      'Consider client continuity'
    ]
  },
  term_time: {
    key: 'term_time',
    name: 'Term-Time Only',
    icon: '🎒',
    description: 'Work during school terms only',
    examples: 'e.g., Take unpaid leave during school holidays',
    considerations: [
      'How would holiday cover be arranged?',
      'Impact on annual leave entitlement',
      'Consider busy periods that coincide with holidays'
    ]
  },
  other: {
    key: 'other',
    name: 'Other Arrangement',
    icon: '✨',
    description: 'A custom flexible working pattern',
    examples: 'Describe your proposed arrangement in detail',
    considerations: [
      'Be specific about what you are proposing',
      'Explain how it would work in practice',
      'Consider the impact on colleagues and clients'
    ]
  }
};


// ============================================================
// MAIN COMPONENT
// ============================================================

const FlexibleWorkingRequestForm = ({ employeeId, employeeData, onSuccess, onCancel }) => {
  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedType, setSelectedType] = useState('');
  const [formData, setFormData] = useState({});
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [showHelp, setShowHelp] = useState(false);

  const typeConfig = selectedType ? FLEXIBILITY_TYPES[selectedType] : null;
  const steps = ['Check Eligibility', 'Your Request', 'Review & Submit'];

  // Check eligibility on mount
  useEffect(() => {
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    try {
      setLoading(true);
      const response = await api.get('/flexible-working/eligibility', {
        params: { employee_id: employeeId }
      });
      setEligibility(response.data);
    } catch (err) {
      // Fallback - assume eligible (day-one right)
      setEligibility({
        eligible: true,
        requests_remaining: 2,
        day_one_right: true,
        messages: ['Day-one right applies - you can make this request']
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        employee_id: employeeId,
        flexibility_type: selectedType,
        current_pattern: formData.current_pattern,
        requested_pattern: formData.requested_pattern,
        proposed_start_date: formData.proposed_start_date,
        employee_reason: formData.employee_reason,
        employee_impact_notes: formData.employee_impact_notes
      };

      const response = await api.post('/flexible-working/requests', payload);
      
      setSnackbar({
        open: true,
        message: `Request submitted! Response deadline: ${response.data.response_deadline_display}`,
        severity: 'success'
      });

      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // RENDER FUNCTIONS
  // ============================================================

  // Step 0: Eligibility Check
  const renderStepZero = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Your Right to Request Flexible Working
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Key Info Card */}
          <Card variant="outlined" sx={{ mb: 3, backgroundColor: 'info.lighter' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                📋 What You Need to Know
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Day-one right" 
                    secondary="You can request flexible working from your first day of employment"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Schedule color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="2 requests per year" 
                    secondary="You can make up to 2 flexible working requests in any 12-month period"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><AccessTime color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="2-month response deadline" 
                    secondary="Your employer must respond within 2 months of your request"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Gavel color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Consultation required" 
                    secondary="Your employer must discuss your request with you before making a decision"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Eligibility Status */}
          <Card 
            variant="outlined" 
            sx={{ 
              mb: 3, 
              borderColor: eligibility?.eligible ? 'success.main' : 'error.main',
              backgroundColor: eligibility?.eligible ? 'success.lighter' : 'error.lighter'
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                {eligibility?.eligible ? (
                  <CheckCircle color="success" />
                ) : (
                  <Cancel color="error" />
                )}
                <Typography variant="h6">
                  {eligibility?.eligible ? 'You Can Make a Request' : 'Request Limit Reached'}
                </Typography>
              </Box>

              {eligibility?.messages?.map((msg, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  {msg}
                </Typography>
              ))}

              {eligibility?.eligible && (
                <Chip 
                  label={`${eligibility.requests_remaining} request(s) remaining this year`}
                  color="success"
                  sx={{ mt: 2 }}
                />
              )}
            </CardContent>
          </Card>

          {/* Refusal Reasons Info */}
          <Box mb={2}>
            <Button
              onClick={() => setShowHelp(!showHelp)}
              endIcon={showHelp ? <ExpandLess /> : <ExpandMore />}
              size="small"
            >
              {showHelp ? 'Hide' : 'Show'} reasons an employer can refuse
            </Button>
          </Box>

          <Collapse in={showHelp}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  8 Statutory Reasons for Refusal
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Your employer can only refuse your request for one of these business reasons:
                </Typography>
                <List dense>
                  {[
                    'Burden of additional costs',
                    'Detrimental effect on ability to meet customer demand',
                    'Inability to reorganise work among existing staff',
                    'Inability to recruit additional staff',
                    'Detrimental impact on quality',
                    'Detrimental impact on performance',
                    'Insufficiency of work during proposed hours',
                    'Planned structural changes'
                  ].map((reason, i) => (
                    <ListItem key={i} sx={{ py: 0 }}>
                      <ListItemText 
                        primary={`${i + 1}. ${reason}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Collapse>
        </>
      )}
    </Box>
  );

  // Step 1: Request Details
  const renderStepOne = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        What Flexible Working Do You Need?
      </Typography>

      {/* Type Selection */}
      <Typography variant="subtitle2" gutterBottom color="text.secondary">
        Select the type of flexibility you're requesting:
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {Object.values(FLEXIBILITY_TYPES).map(type => (
          <Grid item xs={12} sm={6} md={4} key={type.key}>
            <Card
              variant={selectedType === type.key ? 'elevation' : 'outlined'}
              sx={{
                cursor: 'pointer',
                borderColor: selectedType === type.key ? 'primary.main' : 'divider',
                borderWidth: selectedType === type.key ? 2 : 1,
                backgroundColor: selectedType === type.key ? 'primary.lighter' : 'background.paper',
                height: '100%',
                '&:hover': { borderColor: 'primary.light' }
              }}
              onClick={() => setSelectedType(type.key)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h5">{type.icon}</Typography>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {type.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {type.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Type-specific guidance */}
      {typeConfig && (
        <Collapse in={!!selectedType}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>{typeConfig.name}</AlertTitle>
            <Typography variant="body2" gutterBottom>
              <strong>Example:</strong> {typeConfig.examples}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Things to consider:</strong>
            </Typography>
            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
              {typeConfig.considerations.map((c, i) => (
                <li key={i}><Typography variant="body2">{c}</Typography></li>
              ))}
            </ul>
          </Alert>
        </Collapse>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Pattern Details */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Your Current Working Pattern"
            value={formData.current_pattern || ''}
            onChange={(e) => handleFieldChange('current_pattern', e.target.value)}
            placeholder="Describe your current hours, days, and location of work..."
            helperText="e.g., Monday to Friday, 9am-5pm, based at the salon"
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Your Proposed Working Pattern"
            value={formData.requested_pattern || ''}
            onChange={(e) => handleFieldChange('requested_pattern', e.target.value)}
            placeholder="Describe exactly what you're requesting..."
            helperText="Be specific about days, hours, location, and how this would work"
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="When Would You Like This to Start?"
            value={formData.proposed_start_date || ''}
            onChange={(e) => handleFieldChange('proposed_start_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            helperText="Allow time for the consultation process"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Reasons and Impact */}
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Supporting Information
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        This isn't required, but it helps your employer understand your request.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Why Are You Making This Request? (Optional)"
            value={formData.employee_reason || ''}
            onChange={(e) => handleFieldChange('employee_reason', e.target.value)}
            placeholder="You don't have to give a reason, but it may help your case..."
            helperText="Common reasons include childcare, caring responsibilities, health, study, or work-life balance"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="How Could This Work for the Business? (Optional)"
            value={formData.employee_impact_notes || ''}
            onChange={(e) => handleFieldChange('employee_impact_notes', e.target.value)}
            placeholder="Your thoughts on how the business could accommodate this..."
            helperText="Showing you've considered the impact can strengthen your request"
          />
        </Grid>
      </Grid>
    </Box>
  );

  // Step 2: Review & Submit
  const renderStepTwo = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Your Request
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Type of Request</Typography>
              <Typography variant="body1" fontWeight="medium">
                {typeConfig?.icon} {typeConfig?.name}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Proposed Start Date</Typography>
              <Typography variant="body1">
                {formData.proposed_start_date 
                  ? new Date(formData.proposed_start_date).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })
                  : 'Not specified'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">Current Pattern</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {formData.current_pattern || 'Not provided'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">Requested Pattern</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {formData.requested_pattern || 'Not provided'}
              </Typography>
            </Grid>

            {formData.employee_reason && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">Your Reason</Typography>
                <Typography variant="body2">{formData.employee_reason}</Typography>
              </Grid>
            )}

            {formData.employee_impact_notes && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Business Impact Notes</Typography>
                <Typography variant="body2">{formData.employee_impact_notes}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* What Happens Next */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>What Happens Next?</AlertTitle>
        <List dense>
          <ListItem sx={{ py: 0 }}>
            <ListItemText primary="1. Your employer will acknowledge your request" />
          </ListItem>
          <ListItem sx={{ py: 0 }}>
            <ListItemText primary="2. They must discuss it with you (consultation)" />
          </ListItem>
          <ListItem sx={{ py: 0 }}>
            <ListItemText primary="3. A decision must be made within 2 months" />
          </ListItem>
          <ListItem sx={{ py: 0 }}>
            <ListItemText primary="4. If refused, you have the right to appeal" />
          </ListItem>
        </List>
      </Alert>

      {/* Declaration */}
      <Card variant="outlined" sx={{ backgroundColor: 'grey.50' }}>
        <CardContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.declaration || false}
                onChange={(e) => handleFieldChange('declaration', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I confirm this is a formal flexible working request under the Employment Rights Act 1996. 
                I understand this counts as one of my two requests allowed per 12-month period.
              </Typography>
            }
          />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );

  // Validation for navigation
  const canProceed = () => {
    if (activeStep === 0) return eligibility?.eligible;
    if (activeStep === 1) {
      return selectedType && 
             formData.current_pattern && 
             formData.requested_pattern && 
             formData.proposed_start_date;
    }
    return true;
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <Paper sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        Request Flexible Working
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Box sx={{ minHeight: 400 }}>
        {activeStep === 0 && renderStepZero()}
        {activeStep === 1 && renderStepOne()}
        {activeStep === 2 && renderStepTwo()}
      </Box>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          onClick={() => activeStep === 0 ? onCancel?.() : setActiveStep(prev => prev - 1)}
          startIcon={<ArrowBack />}
        >
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        {activeStep < 2 ? (
          <Button
            variant="contained"
            onClick={() => setActiveStep(prev => prev + 1)}
            endIcon={<ArrowForward />}
            disabled={!canProceed()}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitting || !formData.declaration}
            startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default FlexibleWorkingRequestForm;